"use client";

import { ArrowRight } from "lucide-react";
import { ArticleCard } from "../../components/activities/ArticleCard";
import { ArticleCardCompact } from "../../components/activities/ArticleCardCompact";
import { ArticleSummaryCard } from "../../components/home/cards/ArticleSummaryCard";
import type { LatestArticleGridComponent } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { t, type UiStringKey } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import {
	getImageObjectPositionStyle,
	getImageOverrideProps,
	mergeClassNames,
	resolveImageValue,
} from "../../utils/imageOptions";
import { governorPortraitObjectPosition, governorPortraitZoom } from "../../utils/governorPortrait";

/**
 * "Latest articles" grid. Two visual modes, both CMS-driven:
 *   - cardStyle = "image"   (default): full ArticleCard with hero image, author,
 *                                       excerpt, and social row. Today's behavior.
 *   - cardStyle = "summary"          : compact `ArticleSummaryCard` matching the
 *                                       home design — document icon, tag, date,
 *                                       blue bold title, right-side arrow.
 *
 * When `backgroundImageSrc` is set, the section sits on a faded watermark with
 * the same isolation/overlay pattern used by `PublicationsUpdates`, so the cards
 * stand out against brand imagery.
 *
 * Title and CTA are optional — when both are unset the renderer is just a grid.
 */
// The "À lire aussi" / "Historique récent" strips are INJECTED by the deploy
// projector with baked French labels — they never pass through the per-field
// translation pipeline. Known defaults are swapped per locale here; an
// editor-customized label won't match and renders verbatim.
const INJECTED_LABEL_KEYS: Record<string, UiStringKey> = {
	"Historique récent": "recentHistory",
	"À lire aussi": "readAlso",
	"Voir : Tous les articles": "viewAllArticles",
};

function localizeInjectedLabel(value: string | undefined, locale: string): string | undefined {
	if (!value) return value;
	const key = INJECTED_LABEL_KEYS[value.trim()];
	return key ? t(locale, key) : value;
}

/** The gouverneur "Historique récent" strip — the ONLY compact grid that gets the
 *  4-up portrait treatment (2 cards per row on mobile, 4 on desktop, portrait card
 *  images). Matched by the strip's fragment key, which is `latest-{type}` and is the
 *  canonical, environment-stable key for the governor type. Every other record type's
 *  strip keeps the default compact grid + wide card; nothing else on the site changes. */
const GOVERNOR_HISTORIQUE_FRAGMENT_KEY = "latest-gouverneur";

export function LatestArticleGridRenderer({ component }: { component: LatestArticleGridComponent }) {
	const locale = useLocale();
	const { items, ctaHref, cardStyle, backgroundImageSrc, containerStyle, fragmentKey } =
		component.content;
	// `showDate: false` hides the grey date on the cards. Set by the injected
	// "Historique récent" strip of the publication listings, never by an editorial
	// strip: "À lire aussi" on an article keeps its date.
	const showDate = component.content.showDate !== false;
	const title = localizeInjectedLabel(component.content.title, locale);
	const ctaLabel = localizeInjectedLabel(component.content.ctaLabel, locale);
	const resolvedCtaHref = resolveLinkHref(ctaHref);
	const useSummary = cardStyle === "summary";
	const useCompact = cardStyle === "compact";
	const hasBackground = Boolean(backgroundImageSrc);
	const isInline = containerStyle === "inline";
	const isBare = containerStyle === "bare";
	// Governor-ONLY: narrower 4-up portrait grid for the gouverneur recent-history strip.
	const isGovernorHistorique = useCompact && fragmentKey === GOVERNOR_HISTORIQUE_FRAGMENT_KEY;
	const gridCols = isGovernorHistorique ? "grid-cols-2 md:grid-cols-4" : "md:grid-cols-3";

	const grid = (
		<div className={`grid gap-4 sm:gap-6 ${gridCols} ${useSummary ? "" : "mt-4"}`}>
			{items.map((item, index) => {
				if (useSummary) {
					return (
						<ArticleSummaryCard
							key={`${item.title}-${index}`}
							tag={item.category}
							date={showDate ? item.date : ""}
							title={item.title}
							href={item.href}
						/>
					);
				}
				if (useCompact) {
					const governorImageSrc = isGovernorHistorique
						? resolveImageValue(item.imageSrc).src
						: undefined;
					return (
						<ArticleCardCompact
							key={`${item.title}-${index}`}
							{...item}
							showDate={showDate}
							portrait={isGovernorHistorique}
							imagePosition={
								isGovernorHistorique ? governorPortraitObjectPosition(governorImageSrc) : undefined
							}
							imageZoom={isGovernorHistorique ? governorPortraitZoom(governorImageSrc) : undefined}
						/>
					);
				}
				return <ArticleCard key={`${item.title}-${index}`} {...item} />;
			})}
		</div>
	);

	const cta = resolvedCtaHref && ctaLabel ? <CtaLink href={resolvedCtaHref} label={ctaLabel} /> : null;
	const heading = title ? (
		<h3 className="text-[26px] font-bold text-[#005CA9] md:text-[28px]">{title}</h3>
	) : null;

	const inner = (
		<div className="relative mx-auto max-w-7xl px-4 md:px-8">
			{heading}
			<div className={heading ? "mt-5" : ""}>{grid}</div>
			{cta && <div className="mt-4">{cta}</div>}
		</div>
	);

	if (hasBackground) {
		return <BackgroundFrame imageSrc={backgroundImageSrc!}>{inner}</BackgroundFrame>;
	}

	// `containerStyle: "bare"` — inherit the page container's width/padding with
	// NO self `px`/`max-w` wrapper and NO divider line. The section separation is
	// provided by the surrounding page (its own SectionHeading + PageRenderer
	// spacing), so the grid lines up flush with the other sections.
	if (isBare) {
		return (
			<section className="mt-10 md:mt-14">
				{heading}
				<div className={heading ? "mt-5" : ""}>{grid}</div>
				{cta && <div className="mt-4">{cta}</div>}
			</section>
		);
	}

	// `containerStyle: "inline"` — designed for article-page footers. We
	// drop the `mx-auto max-w-7xl px-4 md:px-8` self-wrapper (the parent
	// page already provides a max-width / padding) so the cards line up
	// with the article body width instead of collapsing into a narrower
	// inner band. A top divider + generous top padding push the section
	// visibly away from the body paragraphs so it reads as its own block.
	if (isInline) {
		return (
			<section className="mt-10 border-t border-slate-200 pt-8 md:mt-12 md:pt-10">
				{heading}
				<div className={heading ? "mt-5" : ""}>{grid}</div>
				{cta && <div className="mt-4">{cta}</div>}
			</section>
		);
	}

	// No background — minimal section, preserves prior layout for existing pages
	// that don't ship a watermark.
	return <section>{cta && !heading ? <div className="mb-2">{cta}</div> : null}{heading ? inner : <div className="px-4 md:px-8">{grid}{cta && <div className="mt-4">{cta}</div>}</div>}</section>;
}

/** Yellow-arrow text link (e.g., "Tous les articles"). Same affordance used by
 *  the publications/news sections so the home reads as one design system. */
function CtaLink({ href, label }: { href: string; label: string }) {
	return (
		<RuntimeLink
			className="inline-flex items-center gap-2 text-[18px] xl:text-[20px] text-[#0069B4] hover:text-[#005CA9]"
			href={href}
		>
			{label} <ArrowRight className="h-4 w-4 text-[#FFA401]" />
		</RuntimeLink>
	);
}

/** Wraps the section with a faded watermark + white overlay.
 *
 *  Two layout details worth understanding:
 *  - `mx-[calc(50%-50vw)]` makes the section span the full viewport even when
 *    the parent <main> has a max-width (inner pages). On the home (fluid mode)
 *    it's a no-op, but having it here means the same component works in both.
 *  - `-mt-6 md:-mt-6 lg:-mt-6` absorbs the inter-component margin that
 *    PageRenderer applies to LatestArticleGrid, so the watermark band sits
 *    flush against the previous section (the dark blue search band) — no
 *    transparent gap stripe between them. The matching value comes from
 *    PageRenderer's spacingClassesByType for "LatestArticleGrid" (mt-6).
 *
 *  The image is desaturated with `filter: grayscale(1)` so the watermark
 *  reads as a neutral gray texture (no brand colors competing with the cards),
 *  and the overlay sits at 0.85 — lower than the colorized variant because the
 *  desaturated source needs less wash to fade into a soft background. */
function BackgroundFrame({
	imageSrc,
	children,
}: {
	imageSrc: NonNullable<LatestArticleGridComponent["content"]["backgroundImageSrc"]>;
	children: React.ReactNode;
}) {
	const { src, options } = resolveImageValue(imageSrc);
	const backgroundSrc = src || "/media/welcome_background.jpg";
	const objectPositionStyle = getImageObjectPositionStyle(options);
	const overrideProps = getImageOverrideProps(options);

	return (
		<section
			className={mergeClassNames(
				"relative z-0 isolate -mt-6 mx-[calc(50%-50vw)]",
				overrideProps.className
			)}
			style={overrideProps.style}
		>
			<div className="absolute inset-0" aria-hidden="true">
				<div className="relative h-full w-full">
					<RuntimeImage
						src={backgroundSrc}
						alt=""
						className="absolute inset-0 h-full w-full object-cover object-center"
						style={{ ...objectPositionStyle, filter: "grayscale(1)" }}
					/>
				</div>
				<div className="absolute inset-0" style={{ background: "lab(100 0 0 / 0.85)" }} aria-hidden="true" />
			</div>
			<div className="relative py-12">{children}</div>
		</section>
	);
}
